import { createAction, createAsyncThunk, type CaseReducer, type PayloadAction } from '@reduxjs/toolkit';
import { Field, FieldValidationLevel, RFQTradingProtocolEnum, SideEnum, type FieldValidationRule } from '@talos/kyoko';
import { isBefore } from 'date-fns';
import type { WritableDraft } from 'immer';

import type { AppExtraArgument, AppState } from '../../../../providers/AppStateProvider/types';
import { getDefaultOTCRepoRFQCurrency, getOTCRepoQuantityCurrencyOptions, rfqCurrencyEquals } from '../currencies';
import { initializeRFQMarketsFieldThunk } from '../rfqMarketInitialization';
import type { RFQState } from '../types';
import { otcRepoSelectors } from './otcRepoSelectors';
import type { OTCRepoFormState } from './otcRepoTypes';

interface OTCRepoFormReducers {
  setOTCRepoCashCurrency: CaseReducer<RFQState, PayloadAction<string | undefined>>;
  setOTCRepoCollateralAsset: CaseReducer<RFQState, PayloadAction<string | undefined>>;
  setOTCRepoStartTime: CaseReducer<RFQState, PayloadAction<string | undefined>>;
  setOTCRepoEndTime: CaseReducer<RFQState, PayloadAction<string | undefined>>;
}

const endTimeIsNotBeforeStartTime: FieldValidationRule<Field<string>, WritableDraft<OTCRepoFormState>, string> = (
  field,
  context
) => {
  if (!field.value || !context?.startTimeField.value) {
    return null;
  }

  if (isBefore(new Date(field.value), new Date(context.startTimeField.value))) {
    return {
      message: 'Maturity Date cannot be before Effective Date',
      level: FieldValidationLevel.Error,
    };
  }

  return null;
};

function validateOTCRepoEndTime(state: WritableDraft<RFQState>) {
  state.otcRepoForm.endTimeField = state.otcRepoForm.endTimeField.validate(
    [endTimeIsNotBeforeStartTime],
    state.otcRepoForm
  );
}

function syncOTCRepoRFQCurrency(
  state: WritableDraft<RFQState>,
  { preserveCurrentSelection }: { preserveCurrentSelection: boolean }
) {
  const quantityCurrencyOptions = getOTCRepoQuantityCurrencyOptions({
    cashCurrency: state.otcRepoForm.cashCurrencyField.value,
    collateralAsset: state.otcRepoForm.collateralAssetField.value,
  });
  const selectedCurrency = state.form.rfqCurrencyField.value;
  const selectedCurrencyIsStillAvailable =
    selectedCurrency != null &&
    quantityCurrencyOptions.some(option => rfqCurrencyEquals(option.currency, selectedCurrency));

  if (preserveCurrentSelection && selectedCurrencyIsStillAvailable) {
    return;
  }

  state.form.rfqCurrencyField = state.form.rfqCurrencyField.updateValue(
    getDefaultOTCRepoRFQCurrency(quantityCurrencyOptions),
    true
  );
}

export const getInitialOTCRepoFormState = (): OTCRepoFormState => ({
  cashCurrencyField: new Field<string>({ name: 'Cash Currency', isRequired: true }),
  collateralAssetField: new Field<string>({ name: 'Collateral Asset', isRequired: true }),
  startTimeField: new Field<string>({ name: 'Start Time', isRequired: true }),
  endTimeField: new Field<string>({ name: 'End Time', isRequired: true }),
});

export const otcRepoFormReducers: OTCRepoFormReducers = {
  setOTCRepoCashCurrency: (state, action) => {
    state.otcRepoForm.cashCurrencyField = state.otcRepoForm.cashCurrencyField.updateValue(action.payload);
    syncOTCRepoRFQCurrency(state, { preserveCurrentSelection: true });
  },
  setOTCRepoCollateralAsset: (state, action) => {
    state.otcRepoForm.collateralAssetField = state.otcRepoForm.collateralAssetField.updateValue(action.payload);
    syncOTCRepoRFQCurrency(state, { preserveCurrentSelection: false });
  },
  setOTCRepoStartTime: (state, action) => {
    state.otcRepoForm.startTimeField = state.otcRepoForm.startTimeField.updateValue(action.payload);
    validateOTCRepoEndTime(state);
  },
  setOTCRepoEndTime: (state, action) => {
    state.otcRepoForm.endTimeField = state.otcRepoForm.endTimeField.updateValue(action.payload);
    validateOTCRepoEndTime(state);
  },
};

const setOTCRepoCashCurrencyAction = createAction<string | undefined>('RFQ/setOTCRepoCashCurrency');
const setOTCRepoCollateralAssetAction = createAction<string | undefined>('RFQ/setOTCRepoCollateralAsset');
const setOTCRepoStartTime = createAction<string | undefined>('RFQ/setOTCRepoStartTime');
const setOTCRepoEndTime = createAction<string | undefined>('RFQ/setOTCRepoEndTime');

function shouldInitializeRFQMarkets(wasOTCRepoSelectionComplete: boolean, state: AppState): boolean {
  const isOTCRepoSelectionComplete = otcRepoSelectors.selectHasAssetSelection(state);
  return !wasOTCRepoSelectionComplete && isOTCRepoSelectionComplete && !state.rfq.form.rfqMarketsField.initialized;
}

export const setOTCRepoCashCurrency = createAsyncThunk<
  void,
  string | undefined,
  { state: AppState; extra: AppExtraArgument }
>('rfqSlice/setOTCRepoCashCurrencyThunk', (cashCurrency, thunk) => {
  // a bit awkward, heuristic to track if we are already mid-init of the markets field. Can improve in the future...
  const wasOTCRepoSelectionComplete = otcRepoSelectors.selectHasAssetSelection(thunk.getState());

  thunk.dispatch(setOTCRepoCashCurrencyAction(cashCurrency));
  if (shouldInitializeRFQMarkets(wasOTCRepoSelectionComplete, thunk.getState())) {
    thunk.dispatch(initializeRFQMarketsFieldThunk({ desiredValue: undefined }));
  }
});

export const setOTCRepoCollateralAsset = createAsyncThunk<
  void,
  string | undefined,
  { state: AppState; extra: AppExtraArgument }
>('rfqSlice/setOTCRepoCollateralAssetThunk', (collateralAsset, thunk) => {
  const wasOTCRepoSelectionComplete = otcRepoSelectors.selectHasAssetSelection(thunk.getState());

  thunk.dispatch(setOTCRepoCollateralAssetAction(collateralAsset));
  if (shouldInitializeRFQMarkets(wasOTCRepoSelectionComplete, thunk.getState())) {
    thunk.dispatch(initializeRFQMarketsFieldThunk({ desiredValue: undefined }));
  }
});

/**
 * This function sets a few properties correctly for the product type. The user won't be able to see / change them.
 * If the product group is changed to something else, we don't have to worry about clean-up of whatever we do in here,
 * since we always reset the rfq form in that case.
 */
export function initOTCRepoStateOnProductGroupSet(state: WritableDraft<RFQState>) {
  state.form.rfqTradingProtocolField = state.form.rfqTradingProtocolField.updateValue(
    RFQTradingProtocolEnum.Negotiated
  );
  state.form.pricingModeField = state.form.pricingModeField.setIsVisible(false);
  state.form.sideField = state.form.sideField.updateValue(SideEnum.Buy);
}

export function touchOTCRepoForm(state: WritableDraft<RFQState>) {
  state.otcRepoForm.cashCurrencyField = state.otcRepoForm.cashCurrencyField.setTouched(true);
  state.otcRepoForm.collateralAssetField = state.otcRepoForm.collateralAssetField.setTouched(true);
  state.otcRepoForm.startTimeField = state.otcRepoForm.startTimeField.setTouched(true);
  state.otcRepoForm.endTimeField = state.otcRepoForm.endTimeField.setTouched(true);
}

export const rfqOTCRepoSliceActions = {
  setOTCRepoCashCurrency,
  setOTCRepoCollateralAsset,
  setOTCRepoStartTime,
  setOTCRepoEndTime,
};

export { otcRepoSelectors };
